import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Input from '../components/Input';
import Loader from '../components/Loader';
import Card from '../components/Card';
import { Check, ArrowRight, ArrowLeft, Info, RotateCcw, AlertTriangle, Search, X, Languages } from 'lucide-react';
import {
  createProject,
  getSitemap,
  analyseUrl,
  updateProject,
  getPageContent,
  geminiAnalyse,
  createPositioningAnalysis,
  getPositioningAnalyses,
  getAIVisibility,
  getAudits,
  extractTechnicalInfo,
  getProject,
  extractMenu,
  recalculateScores,
  getPageSpeedKey,
  launchVisualAudit,
  getAIRecommendations,
} from '../api';
import { useToast } from '../ToastContext';
import useProject from '../hooks/useProject';

async function fetchBestFavicon(siteUrl) {
  try {
    const urlObj = new URL(siteUrl);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=128`;
  } catch (e) {
    return 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
  }
}

function getDomainFromUrl(urlString) {
  try {
    const u = new URL(urlString);
    const host = (u.hostname || '').toLowerCase();
    return host.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function urlsDomainMismatch(urls, projectBaseUrl) {
  const projectDomain = getDomainFromUrl(projectBaseUrl);
  if (!projectDomain) return { mismatch: false };
  const otherDomains = new Set();
  const matching = urls.filter((u) => {
    const d = getDomainFromUrl(u);
    if (d !== projectDomain) {
      otherDomains.add(d);
      return false;
    }
    return true;
  });
  return {
    mismatch: otherDomains.size > 0,
    projectDomain,
    otherDomains: [...otherDomains],
    matchingCount: matching.length,
    totalCount: urls.length
  };
}

/** Clé stable pour croiser URLs du menu et audits (ignore #fragment, www, slash final). */
function normPageSearch(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function siteUrlMatchKey(u) {
  if (!u || typeof u !== 'string') return '';
  try {
    let s = u.trim();
    try {
      s = decodeURIComponent(s);
    } catch {
      /* ignore */
    }
    const noHash = s.split('#')[0].trim();
    if (!noHash) return '';
    const withScheme =
      noHash.startsWith('http://') || noHash.startsWith('https://')
        ? noHash
        : `https://${noHash.replace(/^\/+/, '')}`;
    const url = new URL(withScheme);
    let host = url.hostname.toLowerCase();
    if (host.startsWith('www.')) host = host.slice(4);
    let path = url.pathname || '/';
    path = path.replace(/\/+$/, '') || '/';
    return `${host}${path}`;
  } catch {
    return '';
  }
}

const LANG_LABELS = {
  fr: 'Français', en: 'Anglais', de: 'Allemand', es: 'Espagnol', it: 'Italien', nl: 'Néerlandais', pt: 'Portugais',
};

function primaryLangLabel(code) {
  if (!code) return '—';
  return LANG_LABELS[code] || code.toUpperCase();
}

const STEP_INFO_CREATE = {
  1: { title: "Saisie de l'URL", description: "Entrez l'URL du site web que vous souhaitez auditer" },
  2: { title: 'Structure & langues', description: 'Détection multilingue (hreflang), puis récupération du sitemap' },
  3: { title: 'Analyse SEO (site)', description: 'Audit SEO on-page des URLs de la langue principale uniquement (title, meta, H1, images, canonical)' },
  4: { title: 'Pages de référence', description: 'Sélectionnez les pages stratégiques : PageSpeed, audit visuel et positionnement IA (GEO) s’appliquent à ce sous-ensemble' },
  5: { title: 'Performance (réf.)', description: 'PageSpeed Insights sur les pages de référence uniquement' },
  6: { title: 'Audit visuel (réf.)', description: "Capture et analyse visuelle IA des pages de référence : UI, CTA, lisibilité" },
  7: { title: 'Positionnement IA', description: 'Analyse GEO (visibilité dans les LLM) à partir des pages de référence' },
  8: { title: 'Génération de la synthèse', description: 'Création du rapport complet avec points forts, faibles et todolist' },
  9: { title: 'Projet créé', description: 'Votre projet est prêt !' }
};

const STEP_INFO_AUDIT = {
  1: { title: "Démarrage de l'audit", description: "Préparation de l'audit complet de votre projet" },
  2: { title: 'Structure & langues', description: 'Détection multilingue (hreflang), puis récupération du sitemap' },
  3: { title: 'Analyse SEO (site)', description: 'Audit SEO on-page des URLs de la langue principale uniquement (title, meta, H1, images, canonical)' },
  4: { title: 'Pages de référence', description: 'Sélectionnez les pages stratégiques : PageSpeed, audit visuel et positionnement IA (GEO) s’appliquent à ce sous-ensemble' },
  5: { title: 'Performance (réf.)', description: 'PageSpeed Insights sur les pages de référence uniquement' },
  6: { title: 'Audit visuel (réf.)', description: "Capture et analyse visuelle IA des pages de référence : UI, CTA, lisibilité" },
  7: { title: 'Positionnement IA', description: 'Analyse GEO (visibilité dans les LLM) à partir des pages de référence' },
  8: { title: 'Génération de la synthèse', description: 'Création du rapport complet avec points forts, faibles et todolist' },
  9: { title: 'Audit terminé', description: 'Votre audit complet est prêt !' }
};

export default function ProjectWizardPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { project: existingProject, loading: projectLoading } = useProject(slug);

  const isCreateMode = !slug;
  const STEP_INFO = isCreateMode ? STEP_INFO_CREATE : STEP_INFO_AUDIT;

  const [step, setStep] = useState(1);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [currentPage, setCurrentPage] = useState('');

  const [project, setProject] = useState(existingProject || null);
  const [sitemapUrls, setSitemapUrls] = useState([]);
  const [seoAudits, setSeoAudits] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [performanceAudits, setPerformanceAudits] = useState([]);

  const [menuLoading, setMenuLoading] = useState(false);
  const [menuUrls, setMenuUrls] = useState([]);
  const [pageSearchQuery, setPageSearchQuery] = useState('');

  // Audit visuel (step 6)
  const [visualAudits, setVisualAudits] = useState([]);
  const [visualAuditLoading, setVisualAuditLoading] = useState(false);
  const [visualAuditAllPages, setVisualAuditAllPages] = useState(false);
  const [visualAuditError, setVisualAuditError] = useState('');
  // PageSpeed key status for step 4 (so user knows why PageSpeed might be skipped)
  const [pagespeedKeyConfigured, setPagespeedKeyConfigured] = useState(null);

  // Track the last failed step for retry
  const [failedStep, setFailedStep] = useState(null);
  // Warning si les URLs du sitemap ne correspondent pas au domaine du projet
  const [domainWarningPending, setDomainWarningPending] = useState(null);
  // Site protégé par .htaccess : formulaire identifiants
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authRetryAction, setAuthRetryAction] = useState(null); // 'sitemap' | 'menu' après soumission du formulaire
  const [multilingualInfo, setMultilingualInfo] = useState(null);

  // Audit step 4 : ne faire la présélection auto qu'une seule fois à l'entrée
  const hasAutoPreselectedStep4Ref = useRef(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (existingProject && !isCreateMode) {
      setProject(existingProject);
      setError('');
      if (step === 1 && existingProject?.slug) {
        setStep(2);
        const baseUrl = existingProject.url?.startsWith('http') ? existingProject.url : `https://${existingProject.url || existingProject.name || ''}`;
        fetchSitemap(existingProject.slug, baseUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingProject, isCreateMode]);

  useEffect(() => {
    if (step !== 4) setPageSearchQuery('');
  }, [step]);

  const sortedSeoAuditsStep4 = useMemo(() => {
    const currentProject = project || existingProject;
    const rootUrl = (currentProject?.url || '').replace(/\/$/, '');
    const rootFirst = seoAudits.find(
      (a) => !a.error && (a.url === rootUrl || a.url === rootUrl + '/' || a.url.replace(/\/$/, '') === rootUrl)
    )?.url;
    const orderPriority = [
      ...(rootFirst ? [rootFirst] : []),
      ...menuUrls.filter((u) => seoAudits.some((a) => a.url === u)),
    ];
    return [...seoAudits].sort((a, b) => {
      const ia = orderPriority.indexOf(a.url);
      const ib = orderPriority.indexOf(b.url);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return 0;
    });
  }, [seoAudits, menuUrls, project, existingProject]);

  const filteredSeoAuditsStep4 = useMemo(() => {
    const q = normPageSearch(pageSearchQuery.trim());
    if (!q) return sortedSeoAuditsStep4;
    return sortedSeoAuditsStep4.filter(
      (audit) =>
        normPageSearch(audit.url).includes(q) ||
        normPageSearch(audit.title).includes(q) ||
        normPageSearch(audit.errorMessage).includes(q)
    );
  }, [sortedSeoAuditsStep4, pageSearchQuery]);

  // Vérifier la clé PageSpeed quand on affiche l'étape 4 (sélection des pages)
  useEffect(() => {
    if (step !== 4) return;
    let cancelled = false;
    getPageSpeedKey()
      .then((data) => { if (!cancelled) setPagespeedKeyConfigured(!!data?.is_configured); })
      .catch(() => { if (!cancelled) setPagespeedKeyConfigured(false); });
    return () => { cancelled = true; };
  }, [step]);

  // Audit uniquement : à l'entrée sur l'étape 4, présélectionner automatiquement URL racine + pages du menu
  useEffect(() => {
    if (step !== 4 || isCreateMode || hasAutoPreselectedStep4Ref.current) return;
    const currentProject = project || existingProject;
    if (!currentProject?.slug || !seoAudits.length) return;

    let cancelled = false;
    hasAutoPreselectedStep4Ref.current = true;

    const rootUrl = (currentProject.url || '').replace(/\/$/, '');
    const rootAudit = seoAudits.find(
      (a) => !a.error && (a.url === rootUrl || a.url === rootUrl + '/' || a.url.replace(/\/$/, '') === rootUrl)
    );
    const rootIfValid = rootAudit ? rootAudit.url : null;

    extractMenu(currentProject.slug)
      .then((result) => {
        if (cancelled || !mountedRef.current) return;
        const extractedMenuUrls = result.menu_urls || [];
        setMenuUrls(extractedMenuUrls);

        const menuKeySet = new Set(extractedMenuUrls.map(siteUrlMatchKey).filter(Boolean));
        const validMenuUrls = seoAudits
          .filter((a) => !a.error && a.url && menuKeySet.has(siteUrlMatchKey(a.url)))
          .map((a) => a.url);
        const rootKey = rootIfValid ? siteUrlMatchKey(rootIfValid) : '';
        const preselection = [
          ...(rootIfValid ? [rootIfValid] : []),
          ...validMenuUrls.filter((u) => siteUrlMatchKey(u) !== rootKey)
        ];
        setSelectedPages(preselection);
      })
      .catch(() => {
        if (cancelled || !mountedRef.current) return;
        if (rootIfValid) setSelectedPages([rootIfValid]);
      });

    return () => { cancelled = true; };
  }, [step, isCreateMode, project, existingProject, seoAudits]);

  const handleStep1Submit = async (e) => {
    if (e) e.preventDefault();
    if (isCreateMode) {
      if (!url.trim()) {
        setError('Merci de renseigner une URL.');
        return;
      }

      setError('');
      setLoading(true);
      setLoadingMessage('Récupération du favicon...');

      try {
        const urlObj = new URL(url.startsWith('http') ? url : 'https://' + url);
        const domain = urlObj.hostname;
        const siteUrl = urlObj.origin;

        let favicon_url = await fetchBestFavicon(siteUrl);
        if (!favicon_url) {
          favicon_url = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
        }

        setLoadingMessage('Création du projet...');
        const newProject = await createProject({
          name: domain,
          url: siteUrl,
          favicon_url
        });
        if (!mountedRef.current) return;
        setProject(newProject);
        setError('');

        setStep(2);
        const baseUrl = (newProject.url && newProject.url.startsWith('http')) ? newProject.url : `https://${newProject.url || newProject.name || ''}`;
        await fetchSitemap(newProject.slug, baseUrl);
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
        setError(`Erreur lors de la création du projet : ${errorMessage}`);
        showToast(`Erreur lors de la création du projet : ${errorMessage}`, 'error');
        setFailedStep(1);
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setLoadingMessage('');
        }
      }
    }
  };

  const startAudit = async () => {
    const projectToUse = project || existingProject;
    if (!projectToUse?.slug) {
      if (!isCreateMode && !projectLoading) {
        setError('Projet non disponible. Veuillez recharger la page.');
        showToast('Projet non disponible', 'error');
      }
      return;
    }
    setError('');
    setStep(2);
    const baseUrl = projectToUse.url?.startsWith('http') ? projectToUse.url : `https://${projectToUse.url || projectToUse.name || ''}`;
    await fetchSitemap(projectToUse.slug, baseUrl);
  };

  const fetchSitemap = async (projectSlug, projectBaseUrl) => {
    setLoading(true);
    setMultilingualInfo(null);
    setLoadingMessage('Détection multilingue et récupération du sitemap…');
    setDomainWarningPending(null);

    const fallbackToMenuUrls = async () => {
      setLoadingMessage('Sitemap introuvable, récupération des URLs du menu...');
      const menuResult = await extractMenu(projectSlug);
      const extractedMenuUrls = (menuResult?.menu_urls || []).filter((u) =>
        typeof u === 'string' &&
        u.trim().length > 0 &&
        (u.startsWith('http://') || u.startsWith('https://'))
      );
      setMenuUrls(extractedMenuUrls);
      setSitemapUrls(extractedMenuUrls);
      if (menuResult?.multilingual) {
        setMultilingualInfo(menuResult.multilingual);
      }

      if (extractedMenuUrls.length === 0) {
        throw new Error("Aucune URL exploitable n'a ete trouvee dans le menu.");
      }

      showToast('Sitemap introuvable: audit poursuivi avec les URLs du menu.', 'warning');
      setStep(3);
      await performSeoAudits(projectSlug, extractedMenuUrls);
    };

    try {
      const sitemap = await getSitemap(projectSlug, { cacheBust: true });
      if (!mountedRef.current) return;
      const validUrls = (sitemap.urls || []).filter(u =>
        typeof u === 'string' && u.trim().length > 0 && (u.startsWith('http://') || u.startsWith('https://'))
      );
      if (sitemap.multilingual?.detected || sitemap.multilingual?.filtered) {
        setMultilingualInfo(sitemap.multilingual);
        if (sitemap.multilingual.filtered) {
          const lang = primaryLangLabel(sitemap.multilingual.primary_lang);
          const kept = sitemap.multilingual.kept_count ?? validUrls.length;
          const skipped = sitemap.multilingual.skipped_count ?? 0;
          showToast(
            `Site multilingue : ${kept} page(s) en ${lang}, ${skipped} URL(s) dans d'autres langues exclue(s).`,
            'info',
          );
        }
      }
      setSitemapUrls(validUrls);

      if (validUrls.length === 0) {
        await fallbackToMenuUrls();
        return;
      }

      if (projectBaseUrl) {
        const check = urlsDomainMismatch(validUrls, projectBaseUrl);
        if (check.mismatch) {
          setLoading(false);
          setDomainWarningPending({
            projectSlug,
            urls: validUrls,
            projectDomain: check.projectDomain,
            otherDomains: check.otherDomains,
            matchingCount: check.matchingCount,
            totalCount: check.totalCount
          });
          showToast('Certaines URLs du sitemap ne correspondent pas au domaine du projet.', 'warning');
          return;
        }
      }

      setStep(3);
      await performSeoAudits(projectSlug, validUrls);
    } catch (err) {
      if (!mountedRef.current) return;
      const detail = err.response?.data?.detail;
      if (detail === 'SITE_AUTH_REQUIRED') {
        setShowAuthForm(true);
        setAuthRetryAction('sitemap');
        setError('');
        setFailedStep(2);
        setLoading(false);
        showToast('Le site (ou le sitemap) est protégé par un identifiant/mot de passe. Saisissez vos identifiants ci-dessous pour y accéder.', 'info');
        return;
      }
      try {
        await fallbackToMenuUrls();
      } catch (menuErr) {
        const menuDetail = menuErr?.response?.data?.detail;
        const menuErrorMessage = menuDetail || menuErr?.message || 'Erreur inconnue';
        setError(`Erreur lors de la récupération du sitemap/menu : ${menuErrorMessage}`);
        showToast(`Erreur lors de la récupération du sitemap/menu : ${menuErrorMessage}`, 'error');
        setFailedStep(2);
        setLoading(false);
      }
    }
  };

  const handleAuthFormSubmit = async (e) => {
    e.preventDefault();
    const currentProject = project || existingProject;
    if (!currentProject?.slug || !authUsername.trim()) {
      showToast('Veuillez saisir au moins l\'identifiant.', 'error');
      return;
    }
    const retryMenu = authRetryAction === 'menu';
    setLoading(true);
    setLoadingMessage(retryMenu ? 'Enregistrement des identifiants et extraction du menu...' : 'Enregistrement des identifiants...');
    try {
      await updateProject(currentProject.slug, {
        basic_auth_username: authUsername.trim(),
        basic_auth_password: authPassword
      });
      if (!mountedRef.current) return;
      setShowAuthForm(false);
      setAuthRetryAction(null);
      setError('');
      setAuthPassword('');
      if (retryMenu) {
        const result = await extractMenu(currentProject.slug);
        const extractedMenuUrls = result.menu_urls || [];
        setMenuUrls(extractedMenuUrls);
        const menuKeySet = new Set(extractedMenuUrls.map(siteUrlMatchKey).filter(Boolean));
        const validMenuUrls = seoAudits
          .filter((a) => !a.error && a.url && menuKeySet.has(siteUrlMatchKey(a.url)))
          .map((a) => a.url);
        setSelectedPages(validMenuUrls);
        showToast(`${validMenuUrls.length} page(s) du menu présélectionnée(s)`, 'success');
      } else {
        const baseUrl = currentProject.url?.startsWith('http') ? currentProject.url : `https://${currentProject.url || currentProject.name || ''}`;
        await fetchSitemap(currentProject.slug, baseUrl);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      setError(`Erreur : ${errorMessage}`);
      showToast(`Erreur : ${errorMessage}`, 'error');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const performSeoAudits = async (projectSlug, urls) => {
    setLoading(true);
    setLoadingMessage('Analyse SEO en cours...');
    const audits = [];
    const totalUrls = urls.length;

    try {
      const batchSize = 5;
      for (let i = 0; i < urls.length; i += batchSize) {
        if (!mountedRef.current) return;
        const batch = urls.slice(i, i + batchSize);
        const currentBatch = Math.min(i + batchSize, totalUrls);
        setLoadingMessage(`Analyse SEO (${currentBatch}/${totalUrls}) pages...`);

        const batchPromises = batch.map(async (batchUrl) => {
          if (mountedRef.current) setCurrentPage(batchUrl);
          try {
            const audit = await analyseUrl(projectSlug, batchUrl, true);
            return { ...audit, url: batchUrl };
          } catch (err) {
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur inconnue';
            const statusCode = err.response?.status;
            return {
              url: batchUrl,
              error: true,
              errorMessage: statusCode ? `${statusCode} ${errorMessage}` : errorMessage
            };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        audits.push(...batchResults);
      }

      if (!mountedRef.current) return;
      setSeoAudits(audits);
      setCurrentPage('');

      // Use current project state directly
      const currentProject = project || existingProject;
      const auditsWithData = audits.filter(a => a.title || a.meta_description || a.h1);
      if (auditsWithData.length > 0 && currentProject?.slug) {
        try {
          setLoadingMessage('Extraction des informations techniques...');
          await extractTechnicalInfo(currentProject.slug, auditsWithData[0].url);
        } catch (err) {
          console.warn('Erreur extraction infos techniques:', err);
        }
      }

      const failedAudits = audits.filter(a => a.error);
      if (failedAudits.length > 0) {
        showToast(`${failedAudits.length} page(s) n'ont pas pu être analysées.`, 'warning');
      }

      setStep(4);
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      setError(`Erreur lors de l'analyse SEO : ${errorMessage}`);
      showToast(`Erreur lors de l'analyse SEO : ${errorMessage}`, 'error');
      setFailedStep(3);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setLoadingMessage('');
        setCurrentPage('');
      }
    }
  };

  const handlePageToggle = (pageUrl) => {
    setSelectedPages(prev =>
      prev.includes(pageUrl)
        ? prev.filter(u => u !== pageUrl)
        : [...prev, pageUrl]
    );
  };

  const handlePreselectMenu = async () => {
    const currentProject = project || existingProject;
    if (!currentProject?.slug) {
      showToast('Projet non disponible', 'error');
      return;
    }

    setMenuLoading(true);
    try {
      const result = await extractMenu(currentProject.slug);
      const extractedMenuUrls = result.menu_urls || [];
      setMenuUrls(extractedMenuUrls);

      const menuKeySet = new Set(extractedMenuUrls.map(siteUrlMatchKey).filter(Boolean));
      const validMenuUrls = seoAudits
        .filter((audit) => !audit.error && audit.url && menuKeySet.has(siteUrlMatchKey(audit.url)))
        .map((a) => a.url);

      setSelectedPages(validMenuUrls);
      showToast(`${validMenuUrls.length} page(s) du menu présélectionnée(s)`, 'success');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'SITE_AUTH_REQUIRED') {
        setShowAuthForm(true);
        setAuthRetryAction('menu');
        showToast('Le site est protégé. Saisissez vos identifiants ci-dessous.', 'info');
      } else {
        const errorMessage = detail || err.message || 'Erreur inconnue';
        showToast(`Erreur lors de l'extraction du menu : ${errorMessage}`, 'error');
      }
    } finally {
      setMenuLoading(false);
    }
  };

  const handleStep4Next = () => {
    const validSelectedPages = selectedPages.filter(pageUrl => {
      const audit = seoAudits.find(a => a.url === pageUrl);
      return audit && !audit.error;
    });

    if (validSelectedPages.length === 0) {
      setError("Veuillez sélectionner au moins une page valide (sans erreur) pour l'analyse de performance et l'analyse de positionnement IA.");
      return;
    }

    if (validSelectedPages.length !== selectedPages.length) {
      showToast(`${selectedPages.length - validSelectedPages.length} page(s) avec erreur ont été exclues de la sélection.`, 'info');
    }

    // Update state AND pass directly to avoid stale closure
    setSelectedPages(validSelectedPages);
    setError('');
    setStep(5);
    performPerformanceAudits(validSelectedPages);
  };

  const performPerformanceAudits = async (pagesToAudit) => {
    const currentProject = project || existingProject;
    if (!currentProject?.slug) {
      setError('Projet non disponible. Veuillez recharger la page.');
      showToast('Projet non disponible', 'error');
      return;
    }

    setError('');
    setLoading(true);
    setLoadingMessage('Enregistrement des pages de référence…');
    const audits = [];

    try {
      const totalPages = pagesToAudit.length;

      if (totalPages === 0) {
        setError("Aucune page sélectionnée pour l'analyse de performance");
        setLoading(false);
        return;
      }

      await saveReferencePages(pagesToAudit);

      // Check PageSpeed key availability
      let hasPageSpeedKey = false;
      try {
        const keyData = await getPageSpeedKey();
        hasPageSpeedKey = !!keyData?.is_configured;
      } catch (e) { /* ignore */ }

      if (!hasPageSpeedKey) {
        setLoadingMessage('Analyse SEO des pages sélectionnées (sans PageSpeed — clé non configurée)...');
        showToast("Clé PageSpeed non configurée. Seules les données SEO seront enregistrées pour les pages sélectionnées.", 'warning');
      }

      const batchSize = 3;
      for (let i = 0; i < pagesToAudit.length; i += batchSize) {
        if (!mountedRef.current) return;
        const batch = pagesToAudit.slice(i, i + batchSize);
        const currentBatch = Math.min(i + batchSize, totalPages);
        setLoadingMessage(`Analyse de performance (${currentBatch}/${totalPages}) pages...`);

        const batchPromises = batch.map(async (pageUrl) => {
          if (mountedRef.current) setCurrentPage(pageUrl);
          try {
            const audit = await analyseUrl(currentProject.slug, pageUrl, false);
            return { ...audit, url: pageUrl };
          } catch (err) {
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur inconnue';
            const statusCode = err.response?.status;
            return {
              url: pageUrl,
              error: true,
              errorMessage: statusCode ? `${statusCode} ${errorMessage}` : errorMessage
            };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        audits.push(...batchResults);
      }

      if (!mountedRef.current) return;
      setPerformanceAudits(audits);
      setCurrentPage('');

      const failedPerfAudits = audits.filter(a => a.error);
      if (failedPerfAudits.length > 0) {
        showToast(`${failedPerfAudits.length} page(s) n'ont pas pu être analysées pour la performance.`, 'warning');
      }

      // Étape 6 : audit visuel (interactif, l'utilisateur continue manuellement)
      setStep(6);
      // Lancer automatiquement l'audit visuel sur la page racine uniquement
      performVisualAudit(pagesToAudit, false);
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      setError(`Erreur lors de l'analyse de performance : ${errorMessage}`);
      showToast(`Erreur lors de l'analyse de performance : ${errorMessage}`, 'error');
      setFailedStep(5);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setLoadingMessage('');
        setCurrentPage('');
      }
    }
  };

  const saveReferencePages = async (pages) => {
    const currentProject = project || existingProject;
    if (!currentProject?.slug) return;
    try {
      await updateProject(currentProject.slug, { reference_pages: pages });
    } catch (err) {
      console.warn('Erreur sauvegarde pages de référence:', err);
    }
  };

  /**
   * Lance l'audit visuel.
   * @param {string[]} referencePages - pages de référence sélectionnées
   * @param {boolean} allPages - si false, analyse uniquement la page racine
   */
  const performVisualAudit = async (referencePages, allPages = false) => {
    const currentProject = project || existingProject;
    if (!currentProject?.slug) return;

    setVisualAuditError('');
    setVisualAuditLoading(true);
    setVisualAudits([]);

    try {
      let urlsToAudit;
      if (allPages) {
        urlsToAudit = referencePages.slice(0, 6);
      } else {
        // Page racine = première page ou URL du projet
        const rootUrl = (currentProject.url || '').replace(/\/$/, '');
        const rootPage =
          referencePages.find(
            (u) => u.replace(/\/$/, '') === rootUrl || u === rootUrl + '/',
          ) || referencePages[0];
        urlsToAudit = rootPage ? [rootPage] : referencePages.slice(0, 1);
      }

      if (!urlsToAudit.length) {
        setVisualAuditError("Aucune page de référence disponible pour l'audit visuel.");
        return;
      }

      const result = await launchVisualAudit(currentProject.slug, urlsToAudit);
      if (mountedRef.current) {
        setVisualAudits(result.pages || []);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const detail = err.response?.data?.detail || err.message || 'Erreur inconnue';
      setVisualAuditError(detail);
    } finally {
      if (mountedRef.current) setVisualAuditLoading(false);
    }
  };

  const handleStep6Next = () => {
    setStep(7);
    performPositioningAnalysis(performanceAudits, selectedPages);
  };

  const performPositioningAnalysis = async (perfAudits = [], pages = []) => {
    const currentProject = project || existingProject;
    if (!currentProject?.slug) {
      setError('Projet non disponible. Veuillez recharger la page.');
      showToast('Projet non disponible', 'error');
      return;
    }

    setError('');
    setLoading(true);
    setLoadingMessage('Analyse de positionnement IA en cours...');

    try {
      const referencePagesData = perfAudits
        .filter(a => pages.includes(a.url) && !a.error && a.title !== undefined)
        .map(audit => ({
          url: audit.url,
          title: audit.title || '',
          meta_description: audit.meta_description || '',
          h1: audit.h1 || '',
          score_global: audit.score_global || 0
        }));

      // Fetch content in parallel instead of sequentially
      setLoadingMessage('Récupération du contenu des pages...');
      const contentPromises = referencePagesData.map(async (page) => {
        try {
          const pageData = await getPageContent(currentProject.slug, page.url);
          return {
            ...page,
            h2: pageData.h2 || [],
            h3: pageData.h3 || [],
            content: pageData.content || '',
            content_preview: pageData.content_preview || '',
            nb_images: pageData.nb_images || 0,
            alt_texts: pageData.alt_texts || [],
            paragraphs: pageData.paragraphs || []
          };
        } catch (error) {
          return page;
        }
      });
      const enrichedPagesData = await Promise.all(contentPromises);

      if (!mountedRef.current) return;
      setLoadingMessage('Analyse IA en cours...');

      const positioningData = {
        reference_pages: pages,
        reference_pages_data: enrichedPagesData
      };

      let analysisCreated = false;
      try {
        await createPositioningAnalysis(currentProject.slug, positioningData);
        analysisCreated = true;
        showToast('Analyse de positionnement IA terminée', 'success');
      } catch (geminiErr) {
        const statusCode = geminiErr.response?.status;
        const errorMessage = geminiErr.response?.data?.detail || geminiErr.response?.data?.error_message || geminiErr.message || 'Erreur inconnue';

        if (statusCode === 403 || errorMessage.includes('Clé Gemini') || errorMessage.includes('non configurée') || errorMessage.includes('API key')) {
          showToast("Clé Gemini non configurée. L'analyse IA sera ignorée. Vous pourrez la configurer plus tard.", 'warning');
        } else if (statusCode === 503 || errorMessage.includes('overloaded') || errorMessage.includes('surchargé')) {
          showToast("Le modèle Gemini est temporairement surchargé. L'analyse IA sera ignorée.", 'warning');
        } else if (statusCode === 504 || errorMessage.includes('Timeout') || errorMessage.includes('timeout')) {
          showToast("L'analyse IA a pris trop de temps. Passage à l'étape suivante...", 'warning');
        } else {
          showToast(`Erreur lors de l'analyse IA: ${errorMessage}. L'analyse continuera sans l'analyse IA.`, 'warning');
        }

        try {
          const existingAnalyses = await getPositioningAnalyses(currentProject.slug);
          if (existingAnalyses && existingAnalyses.length > 0) {
            analysisCreated = true;
          }
        } catch (checkErr) {
          console.warn('Vérification analyses existantes échouée:', checkErr);
        }
      }

      // Même logique que "Lancer l'analyse IA" : déclencher la visibilité IA (Gemini) après l'analyse de positionnement
      if (analysisCreated) {
        try {
          await getAIVisibility(currentProject.slug);
        } catch (visibilityErr) {
          // Ne pas bloquer : la visibilité IA est secondaire
        }
      }

      setStep(8);
      await generateSynthesis();
    } catch (err) {
      if (!mountedRef.current) return;
      showToast("L'analyse IA a été ignorée. Génération de la synthèse...", 'warning');
      setStep(8);
      await generateSynthesis();
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setLoadingMessage('');
      }
    }
  };

  const generateSynthesis = async () => {
    setLoading(true);
    setLoadingMessage('Génération de la synthèse...');

    try {
      const currentProject = project || existingProject;
      if (currentProject?.slug) {
        try {
          await recalculateScores(currentProject.slug);
        } catch (err) {
          console.warn('Erreur recalcul scores:', err);
        }

        try {
          await getAIRecommendations(currentProject.slug, true);
        } catch (recoErr) {
          const recoDetail = recoErr.response?.data?.detail || recoErr.message || '';
          if (
            recoDetail.includes('Gemini') ||
            recoDetail.includes('non configurée') ||
            recoDetail.includes('API key')
          ) {
            showToast('Recommandations SEO IA ignorées (clé Gemini non configurée).', 'warning');
          } else {
            console.warn('Erreur recommandations SEO IA:', recoErr);
          }
        }

        if (seoAudits.length > 0 || performanceAudits.length > 0) {
          try {
            const updatedProject = await getProject(currentProject.slug);
            if (mountedRef.current && updatedProject) {
              setProject(updatedProject);
            }
          } catch (err) {
            console.warn('Erreur mise à jour projet:', err);
          }
        }
      }

      if (mountedRef.current) setStep(9);
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      setError(`Erreur lors de la génération de la synthèse : ${errorMessage}`);
      showToast(`Erreur lors de la génération de la synthèse : ${errorMessage}`, 'error');
      setFailedStep(8);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setLoadingMessage('');
      }
    }
  };

  const confirmDomainWarning = () => {
    if (!domainWarningPending) return;
    const { projectSlug, urls } = domainWarningPending;
    setDomainWarningPending(null);
    setStep(3);
    performSeoAudits(projectSlug, urls);
  };

  const cancelDomainWarning = () => {
    setDomainWarningPending(null);
  };

  const handleRetry = () => {
    setError('');
    const currentProject = project || existingProject;
    if (!currentProject?.slug && failedStep > 1) {
      showToast('Projet non disponible pour relancer', 'error');
      return;
    }
    switch (failedStep) {
      case 1:
        handleStep1Submit();
        break;
      case 2: {
        const baseUrl = currentProject?.url?.startsWith('http') ? currentProject.url : `https://${currentProject?.url || currentProject?.name || ''}`;
        fetchSitemap(currentProject.slug, baseUrl);
        break;
      }
      case 3:
        performSeoAudits(currentProject.slug, sitemapUrls);
        break;
      case 5:
        performPerformanceAudits(selectedPages);
        break;
      case 8:
        generateSynthesis();
        break;
      default:
        break;
    }
    setFailedStep(null);
  };

  const handleFinish = () => {
    const currentProject = project || existingProject;
    navigate(`/projects/${currentProject?.slug}`);
  };

  if (projectLoading && !isCreateMode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader label="Chargement du projet..." />
      </div>
    );
  }

  if (!project && !isCreateMode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-error mb-4">Projet introuvable</p>
          <Button onClick={() => navigate('/projects')}>Retour aux projets</Button>
        </div>
      </div>
    );
  }

  const stepInfo = STEP_INFO[step] || { title: 'Étape inconnue', description: '' };
  const totalSteps = 9;
  const progress = ((step - 1) / (totalSteps - 1)) * 100;

  return (
    <div className="">
      <div className="mx-auto">
        {domainWarningPending && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <div className="flex gap-3">
              <AlertTriangle className="flex-shrink-0 w-6 h-6 text-amber-600" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-1">Attention : domaine du sitemap</h3>
                <p className="text-sm text-amber-800 mb-2">
                  Les URLs du sitemap ne correspondent pas toutes au domaine du projet ({domainWarningPending.projectDomain}).
                  {domainWarningPending.otherDomains?.length > 0 && (
                  <> Autre(s) domaine(s) détecté(s) : <strong>{domainWarningPending.otherDomains.slice(0, 3).join(', ')}{domainWarningPending.otherDomains.length > 3 ? '…' : ''}</strong>.</>
                  )}
                </p>
                <p className="text-sm text-amber-700 mb-4">
                  {domainWarningPending.matchingCount} sur {domainWarningPending.totalCount} URL(s) correspondent au projet. Souhaitez-vous tout de même lancer l&apos;analyse SEO sur toutes les URLs ?
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" onClick={cancelDomainWarning}>
                    Annuler
                  </Button>
                  <Button type="button" variant="primary" onClick={confirmDomainWarning}>
                    Continuer quand même
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
        <div className="mb-4">
          <div className="flex items-start gap-4">
            <Button
              variant="secondary"
              onClick={() => navigate(isCreateMode ? '/projects' : `/projects/${project?.slug}`)}
              className="text-neutral-500 hover:text-neutral-700 px-2 py-1 flex-shrink-0"
            >
              <ArrowLeft size={20} />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-heading font-bold text-blue-gray-900 mb-0">
                  {isCreateMode ? "Création d'un nouveau projet" : 'Audit complet du projet'}
                </h1>
                {project && <span className="text-sm text-neutral-500">• {project.name}</span>}
              </div>
              <div className="flex items-start gap-2 text-xs text-neutral-500">
                <Info size={14} className="mt-0.5 flex-shrink-0 opacity-60" />
                <p>Le processus peut prendre plusieurs minutes selon le nombre de pages à analyser.</p>
              </div>
            </div>
          </div>
        </div>

        <Card className="mb-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-gray-700">Progression</span>
              <span className="text-sm text-blue-gray-500">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-blue-gray-100 rounded-full h-2">
              <div
                className="bg-accent h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((s) => (
                <div key={s} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`relative flex items-center justify-center rounded-full border-2 transition-all duration-300 ${s < step ? 'bg-accent border-accent text-white w-10 h-10' :
                      s === step ? 'border-accent text-white bg-accent w-12 h-12 shadow-lg shadow-accent/30 ring-4 ring-accent/20' :
                        'border-blue-gray-200 text-blue-gray-400 bg-white w-8 h-8'
                      }`}>
                      {s < step ? <Check size={18} /> : <span className={`font-semibold ${s === step ? 'text-base' : 'text-sm'}`}>{s}</span>}
                      {s === step && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-pulse"></span>
                      )}
                    </div>
                    {s === step && (
                      <span className="mt-2 text-xs font-medium text-accent text-center max-w-[60px]">
                        {STEP_INFO[s]?.title.split(' ').slice(0, 2).join(' ')}
                      </span>
                    )}
                  </div>
                  {s < totalSteps && (
                    <div className={`flex-1 h-0.5 mx-1 ${s < step ? 'bg-accent' : 'bg-blue-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="pt-4 border-t border-blue-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse"></div>
                <span className="text-sm font-semibold text-blue-gray-900">
                  Étape {step} sur {totalSteps} : {STEP_INFO[step]?.title || ''}
                </span>
              </div>
              <p className="text-sm text-blue-gray-600 mt-1 ml-4">
                {STEP_INFO[step]?.description}
              </p>
            </div>
          </div>

          <div className="pt-6 border-t border-blue-gray-100 mt-6">
            {loading && (
              <div className="py-8">
                {multilingualInfo?.detected && (step === 2 || step === 3) && (
                  <div className="mb-5 p-4 rounded-xl border border-sky-200 bg-sky-50 text-sky-950">
                    <div className="flex items-start gap-3">
                      <Languages size={20} className="text-sky-700 flex-shrink-0 mt-0.5" aria-hidden />
                      <div className="min-w-0 text-sm leading-relaxed">
                        <p className="font-semibold text-sky-900">Site multilingue détecté</p>
                        {multilingualInfo.hreflang_languages && (
                          <p className="mt-1 text-sky-800">
                            Langues repérées : {multilingualInfo.hreflang_languages}
                          </p>
                        )}
                        {multilingualInfo.filtered ? (
                          <p className="mt-1 text-sky-800">
                            Analyse limitée à la langue principale :{' '}
                            <strong>{primaryLangLabel(multilingualInfo.primary_lang)}</strong>
                            {typeof multilingualInfo.skipped_count === 'number' && multilingualInfo.skipped_count > 0 && (
                              <> — {multilingualInfo.skipped_count} URL(s) dans d&apos;autres langues exclue(s)</>
                            )}
                            {typeof multilingualInfo.kept_count === 'number' && (
                              <> ({multilingualInfo.kept_count} page(s) à auditer)</>
                            )}
                          </p>
                        ) : (
                          <p className="mt-1 text-sky-800">
                            Une seule version linguistique retenue pour l&apos;audit (langue principale du site).
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="text-center mb-6">
                  <Loader label={loadingMessage || 'Chargement...'} />
                </div>
                {currentPage && (
                  <div className="mt-6 p-4 bg-blue-gray-50 rounded-lg border border-blue-gray-200">
                    <p className="text-sm font-medium text-blue-gray-700 mb-2">Page en cours d'analyse :</p>
                    <p className="text-sm text-blue-gray-600 font-mono break-all">{currentPage}</p>
                  </div>
                )}
                {step === 3 && seoAudits.length > 0 && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-blue-gray-600">
                      {seoAudits.length} / {sitemapUrls.length} pages analysées
                    </p>
                  </div>
                )}
                {step === 5 && performanceAudits.length > 0 && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-blue-gray-600">
                      {performanceAudits.length} / {selectedPages.length} pages analysées
                    </p>
                  </div>
                )}
              </div>
            )}

            {!loading && step === 1 && isCreateMode && !showAuthForm && (
              <form onSubmit={handleStep1Submit} className="space-y-4">
                <Input
                  label="URL du site à auditer"
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://www.monsite.com"
                  required
                  autoFocus
                />
                {error && <div className="text-error text-sm">{error}</div>}
                <div className="flex justify-end gap-2 mt-4">
                  <Button type="button" variant="secondary" onClick={() => navigate('/projects')}>
                    Annuler
                  </Button>
                  <Button type="submit" variant="primary">
                    Continuer
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </form>
            )}

            {!loading && showAuthForm && (project || existingProject)?.slug && (
              <form onSubmit={handleAuthFormSubmit} className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-900 font-medium">
                  Le site est protégé par une authentification (ex. .htaccess). Saisissez vos identifiants pour continuer l'audit.
                </p>
                <Input
                  label="Identifiant"
                  type="text"
                  value={authUsername}
                  onChange={e => setAuthUsername(e.target.value)}
                  placeholder="Utilisateur"
                  required
                  autoComplete="username"
                />
                <Input
                  label="Mot de passe"
                  type="password"
                  value={authPassword}
                  onChange={e => setAuthPassword(e.target.value)}
                  placeholder="Mot de passe"
                  autoComplete="current-password"
                />
                {error && <div className="text-error text-sm">{error}</div>}
                <div className="flex justify-end gap-2 mt-4">
                  <Button type="button" variant="secondary" onClick={() => { setShowAuthForm(false); setError(''); }}>
                    Annuler
                  </Button>
                  <Button type="submit" variant="primary">
                    Valider et continuer
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </form>
            )}

            {!loading && step === 4 && (
              <div className="space-y-4">
                {pagespeedKeyConfigured === false && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-semibold text-amber-800 mb-1">
                      Clé Google PageSpeed non configurée
                    </p>
                    <p className="text-sm text-amber-700 mb-2">
                      Les pages que vous sélectionnez ne seront pas analysées par Google PageSpeed tant que la clé API n’est pas renseignée. Seules les données SEO seront enregistrées. Pour activer l’analyse de performance, ajoutez votre clé dans{' '}
                      <button
                        type="button"
                        onClick={() => navigate('/profile')}
                        className="underline font-medium text-amber-900 hover:text-amber-950"
                      >
                        Paramètres du compte (Profil)
                      </button>
                      .
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between gap-4">
                  <p className="text-blue-gray-700">
                    Sélectionnez les pages qui serviront de base pour l'analyse de performance et l'analyse de positionnement IA :
                  </p>
                  <Button
                    variant="secondary"
                    onClick={handlePreselectMenu}
                    disabled={menuLoading}
                    className="flex items-center gap-2 flex-shrink-0"
                  >
                    {menuLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin"></div>
                        Extraction...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        Présélectionner pages du menu
                      </>
                    )}
                  </Button>
                </div>

                {selectedPages.length > 0 && menuUrls.length > 0 && (
                  <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
                    <p className="text-sm text-accent font-semibold">
                      ✓ {selectedPages.length} page(s) présélectionnée(s) depuis le menu de navigation
                    </p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="relative flex-1">
                    <Search
                      size={15}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-gray-400 pointer-events-none"
                    />
                    <input
                      type="search"
                      value={pageSearchQuery}
                      onChange={(e) => setPageSearchQuery(e.target.value)}
                      placeholder="Rechercher une URL ou un titre de page…"
                      className="w-full pl-9 pr-8 py-2.5 text-sm border border-blue-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 bg-white placeholder:text-blue-gray-300"
                      aria-label="Rechercher une page"
                    />
                    {pageSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setPageSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-gray-300 hover:text-blue-gray-600"
                        aria-label="Effacer la recherche"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-blue-gray-500 sm:text-right shrink-0">
                    {selectedPages.length} sélectionnée{selectedPages.length !== 1 ? 's' : ''}
                    {pageSearchQuery.trim() && (
                      <> · {filteredSeoAuditsStep4.length}/{sortedSeoAuditsStep4.length} affichée{filteredSeoAuditsStep4.length !== 1 ? 's' : ''}</>
                    )}
                  </p>
                </div>

                <div className="max-h-96 overflow-y-auto space-y-2 border border-blue-gray-200 rounded-lg p-4">
                  {filteredSeoAuditsStep4.length === 0 ? (
                    <p className="text-sm text-blue-gray-500 text-center py-6">
                      Aucune page ne correspond à « {pageSearchQuery} ».
                    </p>
                  ) : (
                    filteredSeoAuditsStep4.map((audit) => (
                      <div
                        key={audit.url}
                        onClick={() => !audit.error && handlePageToggle(audit.url)}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${audit.error
                          ? 'bg-error/5 border-error/30 cursor-not-allowed opacity-60'
                          : selectedPages.includes(audit.url)
                            ? 'bg-accent/10 border-accent cursor-pointer'
                            : 'bg-white border-blue-gray-200 hover:bg-blue-gray-50 cursor-pointer'
                          }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${audit.error
                          ? 'border-error/50'
                          : selectedPages.includes(audit.url)
                            ? 'bg-accent border-accent'
                            : 'border-blue-gray-300'
                          }`}>
                          {selectedPages.includes(audit.url) && !audit.error && (
                            <Check size={14} className="text-white" />
                          )}
                          {audit.error && <span className="text-error text-xs">⚠</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${audit.error ? 'text-error' : 'text-blue-gray-900'}`}>
                            {audit.url}
                          </p>
                          {audit.error ? (
                            <p className="text-xs text-error mt-1">{audit.errorMessage || "Erreur lors de l'analyse"}</p>
                          ) : audit.title ? (
                            <p className="text-xs text-blue-gray-500 truncate">{audit.title}</p>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {seoAudits.some(a => a.error) && (
                  <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                    <p className="text-sm text-warning font-semibold mb-1">⚠️ Certaines pages n'ont pas pu être analysées</p>
                    <p className="text-xs text-blue-gray-600">
                      {seoAudits.filter(a => a.error).length} page(s) ont échoué. Ces pages ne peuvent pas être sélectionnées pour l'analyse de performance.
                    </p>
                  </div>
                )}
                {error && <div className="text-error text-sm">{error}</div>}
                <div className="flex justify-end gap-2 mt-4">
                  <Button type="button" variant="primary" onClick={handleStep4Next}>
                    Continuer
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* ── Étape 6 : Audit visuel IA ─────────────────────────────────── */}
            {step === 6 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-blue-gray-900 mb-1">Audit visuel IA</h3>
                  <p className="text-sm text-blue-gray-600">
                    Capture d'ecran de la page complete analysee par IA : mise en page, CTA, lisibilite, signaux de confiance.
                  </p>
                </div>

                {/* Option : toutes les pages */}
                <label className="flex items-center gap-3 cursor-pointer select-none group w-fit">
                  <div
                    onClick={() => {
                      const next = !visualAuditAllPages;
                      setVisualAuditAllPages(next);
                      if (!visualAuditLoading) {
                        performVisualAudit(selectedPages, next);
                      }
                    }}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      visualAuditAllPages ? 'bg-accent border-accent' : 'border-blue-gray-300 group-hover:border-accent/60'
                    }`}
                  >
                    {visualAuditAllPages && <Check size={13} className="text-white" />}
                  </div>
                  <span className="text-sm text-blue-gray-700">
                    Analyser toutes les pages de référence
                    {selectedPages.length > 1 && (
                      <span className="text-blue-gray-400 ml-1">({selectedPages.length} pages)</span>
                    )}
                  </span>
                </label>

                {/* Chargement */}
                {visualAuditLoading && (
                  <div className="flex flex-col items-center justify-center py-10 gap-3">
                    <div className="w-8 h-8 border-[3px] border-accent/20 border-t-accent rounded-full animate-spin" />
                    <p className="text-sm text-blue-gray-500">
                      Capture d'écran + analyse IA en cours…
                    </p>
                  </div>
                )}

                {/* Erreur */}
                {!visualAuditLoading && visualAuditError && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <span className="font-semibold">Audit visuel indisponible : </span>
                    {visualAuditError}
                    <p className="text-amber-600 mt-1 text-xs">Vous pouvez continuer sans l'audit visuel.</p>
                  </div>
                )}

                {/* Résultats */}
                {!visualAuditLoading && visualAudits.length > 0 && (
                  <div className="space-y-6">
                    {visualAudits.map((va) => (
                      <div key={va.url} className="border border-blue-gray-200 rounded-xl overflow-hidden">
                        {/* En-tête URL + score */}
                        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-blue-gray-50 border-b border-blue-gray-200">
                          <p className="text-xs font-mono text-blue-gray-600 truncate flex-1">{va.url}</p>
                          {va.analysis?.score_visuel !== undefined && (
                            <span className={`text-sm font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                              va.analysis.score_visuel >= 75 ? 'bg-green-100 text-green-700' :
                              va.analysis.score_visuel >= 50 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {va.analysis.score_visuel}/100
                            </span>
                          )}
                        </div>

                        {va.error ? (
                          <div className="p-4 text-sm text-red-600">{va.error}</div>
                        ) : (
                          <div className="p-4 space-y-4">
                            {/* Ligne : preview + critères */}
                            <div className="flex gap-4">
                              {/* Screenshot */}
                              {va.screenshot_b64 && (
                                <div className="flex-shrink-0">
                                  <img
                                    src={`data:image/png;base64,${va.screenshot_b64}`}
                                    alt={`Capture ${va.url}`}
                                    className="w-44 rounded-lg border border-blue-gray-200 shadow-sm object-cover object-top"
                                    style={{ maxHeight: '120px' }}
                                  />
                                </div>
                              )}

                              {/* Résumé + critères */}
                              <div className="flex-1 min-w-0 space-y-3">
                                {va.analysis?.resume && (
                                  <p className="text-sm text-blue-gray-700 leading-relaxed">{va.analysis.resume}</p>
                                )}
                                {va.analysis?.criteres?.length > 0 && (
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                    {va.analysis.criteres.map((c) => (
                                      <div key={c.cle} className="flex items-center gap-2 min-w-0">
                                        {/* Pastilles de note */}
                                        <div className="flex gap-0.5 flex-shrink-0">
                                          {[1, 2, 3, 4, 5].map((n) => (
                                            <div
                                              key={n}
                                              className={`w-2 h-2 rounded-full ${
                                                n <= c.note
                                                  ? c.note >= 4 ? 'bg-green-500' : c.note >= 3 ? 'bg-amber-400' : 'bg-red-400'
                                                  : 'bg-blue-gray-200'
                                              }`}
                                            />
                                          ))}
                                        </div>
                                        <span className="text-xs text-blue-gray-600 truncate">{c.nom}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Points forts / axes */}
                            {(va.analysis?.points_forts?.length > 0 || va.analysis?.axes_amelioration?.length > 0) && (
                              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-blue-gray-100">
                                {va.analysis?.points_forts?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-green-700 mb-1.5">Points forts</p>
                                    <ul className="space-y-1">
                                      {va.analysis.points_forts.map((p, i) => (
                                        <li key={i} className="text-xs text-blue-gray-600 flex gap-1.5">
                                          <span className="text-green-500 mt-0.5">✓</span>
                                          <span>{p}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {va.analysis?.axes_amelioration?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-amber-700 mb-1.5">À améliorer</p>
                                    <ul className="space-y-1">
                                      {va.analysis.axes_amelioration.map((a, i) => (
                                        <li key={i} className="text-xs text-blue-gray-600 flex gap-1.5">
                                          <span className="text-amber-500 mt-0.5">→</span>
                                          <span>{a}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Recommandation prioritaire */}
                            {va.analysis?.recommandation_prioritaire && (
                              <div className="p-3 bg-accent/8 border border-accent/20 rounded-lg">
                                <p className="text-xs font-semibold text-accent mb-0.5">Recommandation prioritaire</p>
                                <p className="text-xs text-blue-gray-700">{va.analysis.recommandation_prioritaire}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleStep6Next}
                    disabled={visualAuditLoading}
                  >
                    Continuer
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {!loading && step === 9 && (
              <div className="space-y-4 text-center py-8">
                <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-success" />
                </div>
                <h3 className="text-xl font-semibold text-blue-gray-900">
                  {isCreateMode ? 'Projet créé avec succès !' : 'Audit terminé avec succès !'}
                </h3>
                <p className="text-blue-gray-600">
                  {isCreateMode
                    ? 'Votre projet a été créé et toutes les analyses ont été effectuées.'
                    : 'Votre audit complet a été effectué et toutes les analyses ont été réalisées.'}
                </p>
                <div className="mt-6 space-y-2 text-sm text-blue-gray-500">
                  <p>✓ {sitemapUrls.length} pages trouvées dans le sitemap</p>
                  <p>✓ {seoAudits.length} pages analysées (SEO)</p>
                  <p>✓ {selectedPages.length} pages analysées (Performance)</p>
                  {visualAudits.filter(v => !v.error).length > 0 && (
                    <p>✓ Audit visuel IA ({visualAudits.filter(v => !v.error).length} page{visualAudits.filter(v => !v.error).length > 1 ? 's' : ''})</p>
                  )}
                  <p>✓ Analyse de positionnement IA effectuée</p>
                  <p>✓ Synthèse générée</p>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button type="button" variant="primary" onClick={handleFinish}>
                    Voir les résultats
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {error && error.trim() !== '' && (
              <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
                <div className="font-semibold mb-1">⚠️ Erreur</div>
                <div>{error}</div>
                {failedStep && !loading && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleRetry}
                    className="mt-3 flex items-center gap-2"
                  >
                    <RotateCcw size={14} />
                    Réessayer
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
